import React, { useEffect, useState } from "react";
import { App } from '../../app';
import { platformActions } from "../../communication/Communication";
import { Apis } from "../../store/actions";

interface FileInfo {
    transferId: string;
    url: string;
}

interface LinkMap {
    [name: string]: FileInfo;
}


// Update image targets which point to VS-Feedback attachments
// returns the updated html and list of such images so that they can be fetched later
export function updateImages(html: string, isPublic: boolean): [string, LinkMap] {
    if (!html) {
        return [ html, {} ];
    }

    // pattern for VS-Feedback hosted content
    const regImages = /src="(https:\/\/aka\.ms\/(?:dc|dcoxo|dcintppe)\/(?:image|file)\?name=[^&"']{10,200}(?:&amp;|&)tid=)([^"'\s]{10,200})"/g;

    const links: LinkMap = {};
    const newText = html.replace(regImages, (match: string, p1: string, p2: string) => {
        const name = `lnk_img_${p2}`;
        const url = `${p1.replace('&amp;', '&')}${p2}`;

        // eslint-disable-next-line security/detect-object-injection
        links[name] = {
            transferId: p2,
            url: url,
        };
        if (isPublic) {
            return `name="${name}" src="${url}"`;
        } else {
            // To prevent the browser trying to load the image url before we've added the token, we leave the src empty in the rendered html
            return `name="${name}" src="" display=none`;
        }
    });
    return [ newText, links ];
}

// Update anchor (a) targets which point to VS-Feedback attachments
// returns the updated html and list of such links so that they can be fetched later
function updateAnchors(html: string): [string, LinkMap] {
    if (!html) {
        return [html, {}];
    }

    // pattern for VS-Feedback hosted content
    const regAnchors = /href="(https:\/\/aka\.ms\/(?:dc|dcoxo|dcintppe)\/(?:image|file)\?name=[^&"']{10,200}(?:&amp;|&)tid=)([^"'\s]{10,200})"/g;

    const links: LinkMap = {};

    const newText = html.replace(regAnchors, (match: string, p1: string, p2: string) => {
        const name = `lnk_a_${p2}`;
        const url = `${p1.replace('&amp;', '&')}${p2}`;

        // eslint-disable-next-line security/detect-object-injection
        links[name] = {
            transferId: p2,
            url: url,
        };
        return `name="${name}" href="${url}"`;
    });

    return [newText, links];
}

export interface HtmlPreviewProps {
    text: string;
    isPublic: boolean;
    className?: string;
    style?: React.CSSProperties;
}

// Component to render html string with automatic handling of any links which need to be authenticated (private comments etc.)
export default function HtmlPreview({ text, isPublic, className, style } : HtmlPreviewProps): JSX.Element {
    const [finalText, setFinalText] = useState(null);
    const [imagesToFetch, setImagesToFetch] = useState<LinkMap>({});
    const [anchors, setAnchors] = useState<LinkMap>({});

    useEffect(
        () => {
            const escapedHtml = App.EscapeHtmlRawDisplay(text);
            if (isPublic) {
                // nothing to do for public content
                setFinalText(escapedHtml);
                setImagesToFetch({});
                setAnchors({});
            } else {    // Is for private comments etc.
                // Update html to add ids for html nodes (images, anchors) which point to VS-Feedback hosted content so that they can be dynamically updated later
                const [updatedImageText, linksImages] = updateImages(escapedHtml, isPublic);
                const [updatedText, linksAnchors] = updateAnchors(updatedImageText);
                setFinalText(updatedText);
                setImagesToFetch(linksImages);
                setAnchors(linksAnchors);
            }
        }
        , [text, isPublic]
    );


    // Fetch secure images
    useEffect(
        () => {
            const keys = Object.keys(imagesToFetch);
            if (keys.length === 0) {
                return;
            }

            keys.forEach(k => {
                // eslint-disable-next-line security/detect-object-injection
                const curImg = imagesToFetch[k];

                platformActions.get(true, `${Apis.FileBlobGetSecureAccessToken}?transferId=${curImg.transferId}`)
                    .then((req) => {
                        const elements = document.getElementsByName(k);
                        elements.forEach(el => {
                            (el as HTMLIFrameElement).src = `${curImg.url}&token=${req.data}`;
                            (el as HTMLIFrameElement).style.display = "inherit";
                        })
                    }).catch((reason: any) => {
                        console.error(`Error fetching: '${curImg.url}'`);
                    });
            });
        }
        , [imagesToFetch]
    );

    // Update anchor tags to download content in secure way
    useEffect(
        () => {
            const keys = Object.keys(anchors);
            if (keys.length === 0) {
                return;
            }

            const clickHandlers = [];

            keys.forEach(k => {
                // eslint-disable-next-line security/detect-object-injection
                const curAnchor = anchors[k];

                const elements = document.getElementsByName(k);

                // Add event listeners to handle click
                elements.forEach(el => {
                    const elAnchor = el as HTMLAnchorElement;

                    const clickHandler = (e: MouseEvent): boolean => {
                        e.preventDefault();

                        platformActions.get(true, `${Apis.FileBlobGetSecureAccessToken}?transferId=${curAnchor.transferId}`)
                            .then((req) => {
                                App.openSafe(elAnchor.href + `&token=${req.data}`);
                            }).catch((reason: any) => {
                                console.error(`Error fetching: '${elAnchor.href}'`);
                            });

                        return false;
                    }

                    elAnchor.addEventListener("click", clickHandler);
                    clickHandlers.push({ element: elAnchor, clickHandler });
                });
            });

            // Cleanup listeners on unmounting
            return (() => {
                if (clickHandlers) {
                    clickHandlers.forEach(h => {
                        h.element.removeEventListener("click", h.handler);
                    });
                }
            });
        }
        , [anchors]
    );

    return <div
        className={`html-preview ${className ? className : ''}`}
        style={style}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: finalText ?? "" }}
    />
}
